import { _ } from "../../core/polyglot/polyglot.js";
import {
	chartLineIcon,
	circlePlusIcon,
	cloneIcon,
	databaseIcon,
	magicIcon,
} from "../../core/elements/icons/index.js";
import { warnDialog } from "../../core/widgets/dialogs/dialogs.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ActionButtonWidget from "../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import dimensionPickerWidget from "../../core/widgets/dimensionPickerWidget.js";
import displaySettingsWidget from "../reports2/displaySettingsWidget.js";
import popoverActionButtonWidget from "../../core/widgets/popoverActionButtonWidget.js";
import regionWidget from "../../core/widgets/regionWidget.js";

/**
 * This class is responsible for the right action menu of the budget
 * editor. The receiver allows actions such as changing the display
 * settings and the current dimension as well as adding new rows.
 *
 * @param {BudgetReport} budgetReport - The model associated with the document.
 * @param {function} displaySettingsUpdatedCallback - Called with the changed
 *   display settings as arguments.
 * @param {function} dimensionChangedCallback - Called with the selected
 *   dimension as argument.
 * @param {function} accountRowAddedCallback - Called with the selected account
 *   number as arguments.
 * @param {function} onImportDataCallback - Called without parameter
 *   when the user imports data
 * @param {function} onAddIncomeAndCostAccountsCallback - Called without parameter
 *   when the user add the income and costs accounts.
 * @param {function} onSaveBudgetFromForecastId - Called with a forecast id as
 *   parameter when the user chooses a forecast to replace the budget data
 * @param {function} getAllBudgets - Called without parameter to retrieve all
 *   the budgets.
 * @param {function} getAllForecasts - Called without parameter to retrieve all
 *   forecasts
 * @param {boolean} displayBudgetActions - Specify if the budget actions should
 *   be displayed
 */
export default class BudgetEditorActionsWidget extends Widget {
	constructor({
		budgetReport,
		displaySettingsUpdatedCallback,
		dimensionChangedCallback,
		accountRowAddedCallback,
		onImportDataCallback,
		onAddIncomeAndCostAccountsCallback,
		onSaveBudgetFromForecastId,
		getAllBudgets,
		getAllForecasts,
		displayBudgetActions = true,
	}) {
		super(...arguments);

		this._budgetReport = budgetReport;
		this._displaySettingsUpdatedCallback = displaySettingsUpdatedCallback;
		this._dimensionChangedCallback = dimensionChangedCallback;
		this._accountRowAddedCallback = accountRowAddedCallback;

		const displaySettings = displaySettingsWidget({
			displaySettings: this._budgetReport.getDisplaySettings(),
			showExpansionLevel: true,
			showDebitPositiveSwitch: false,
			showPdfSettings: false,
		});

		this._displaySettingsPicker = popoverActionButtonWidget({
			label: _("Display settings"),
			dataTestName: "display-settings",
			popoverHeader: _("Display settings"),
			popoverContent: displaySettings,
			popoverAction: (...rest) =>
				displaySettings.updateDisplaySettings(...rest),
		});

		displaySettings.displaySettingsUpdated.register(
			(newDisplaySettings) => {
				this._budgetReport.setAccountRowVisibility(
					newDisplaySettings.getAccountVisibility(),
				);

				this._displaySettingsUpdatedCallback(newDisplaySettings);
			},
		);

		this._dimensionPicker = dimensionPickerWidget({
			dataTestName: "pick-dimension",
			selectLeavesOnly: true,
		});

		this._dimensionPicker.onDimensionSelected.register((dimension) => {
			this._budgetReport.setDimension(dimension);
			this._dimensionChangedCallback(dimension);
		});

		this._budgetAutomationButton = regionWidget({
			content: (html) => this._renderBudgetAutomationButtonOn(html),
		});

		this._onImportDataCallback = onImportDataCallback;
		this._onSaveBudgetFromForecastId = onSaveBudgetFromForecastId;
		this._allBudgets = undefined;
		this._getAllBudgets = getAllBudgets;
		this._getAllForecasts = getAllForecasts;

		this._onAddIncomeAndCostAccountsCallback =
			onAddIncomeAndCostAccountsCallback;

		this._displayBudgetActions = displayBudgetActions;
	}

	show() {
		Promise.all([this._getAllBudgets(), this._getAllForecasts()]).then(
			([budgets, forecasts]) => {
				this._allBudgets = budgets;
				this._allForecasts = forecasts;
				this._budgetAutomationButton.update();
			},
		);
	}

	updateBudget() {
		this._updateDimension();
		this._budgetAutomationButton.update();
	}

	renderContentOn(html) {
		const budgetSection = this._displayBudgetActions
			? this._sectionRenderer(_("Create Budget From"), (html) =>
					this._renderBudgetSectionOn(html),
				)
			: () => {};

		const accountSection = this._displayBudgetActions
			? this._sectionRenderer(_("Add Account"), (html) =>
					this._renderAddAccountSectionOn(html),
				)
			: () => {};

		html.render(
			this._sectionRenderer(
				_("Report settings"),
				this._displaySettingsPicker,
			),
			budgetSection,
			accountSection,
			this._sectionRenderer(_("Dimension"), this._dimensionPicker),
		);
	}

	_renderBudgetSectionOn(html) {
		html.render(this._budgetAutomationButton);
	}

	_renderBudgetAutomationButtonOn(html) {
		const budget = this._getBudget();

		const klass = `btn btn-block btn-default ${budget ? "" : "disabled"}`;
		const href = budget ? this._linkToBudgetAutomation() : "";

		html.a(
			{ klass, href, "data-test": "budget-from-actuals" },
			magicIcon,
			_("Actuals"),
		);

		html.button(
			{
				klass,
				click: this._showCopyItemsFromOtherBudgetDialog.bind(this),
				"data-test": "import-data-from-another-budget",
			},
			cloneIcon,
			_("Budget"),
		);

		this._renderImportFromForecastButtonOn(html);
	}

	_renderImportFromForecastButtonOn(html) {
		const disabled = !this._allForecasts?.length;

		if (disabled) return;

		html.button(
			{
				class: "btn btn-block btn-default",
				click: () =>
					!disabled && this._showCopyItemsFromForecastDialog(),
				"data-test": "import-data-from-forecast",
			},
			chartLineIcon,
			_("Forecast"),
		);
	}

	_renderAddRowButtonOn(html) {
		if (!this._accountRowAddedCallback) {
			return;
		}

		html.render(
			new ActionButtonWidget({
				label: _("Single account"),
				icon: circlePlusIcon,
				isPrimary: true,
				dataTestName: "add-account-row",
				click: this._showAddAccountRowDialog.bind(this),
			}),
		);
	}

	_showAddAccountRowDialog() {
		const model = {};

		this._configure({
			title: _("Add account"),
			okLabel: _("Add"),
			contentCssClass: "modal-overflow",
			formSpec: {
				model,
				saveAction: () => this._accountRowAddedCallback(model.account),
			},
			formBuilder: (form) => {
				form.newDropDownField({
					label: _("Account"),
					attribute: "account",
					items: this._budgetReport
						.findUnbudgettedAccounts()
						.map((account) => ({
							label: account.toString(),
							value: account.getNumber(),
						})),
				});
			},
		});
	}

	_linkToBudgetAutomation() {
		return this._linkTo(`budget-automation/${this._getBudget().getId()}`);
	}

	_showCopyItemsFromOtherBudgetDialog() {
		if (!this._allBudgets) {
			return;
		}
		const currentBudget = this._getBudget();
		let otherBudget;

		this._configure({
			title: _("Import data from budget"),
			okLabel: _("Import"),
			formSpec: {
				saveAction: () => {
					currentBudget.copyItemsFromBudget(otherBudget);
					return this._onImportDataCallback();
				},
			},
			formBuilder: (form) => {
				form.newDropDownField({
					label: _("Budget"),
					accessor: () => otherBudget,
					mutator: (budget) => {
						otherBudget = budget;
					},
					items: this._allBudgets
						.filter(
							(budget) =>
								budget.getId() !== currentBudget.getId(),
						)
						.map((budget) => ({
							label: `${budget.getName()} - ${budget.periodName()}`,
							value: budget,
						})),
				});
			},
		});
	}

	_showCopyItemsFromForecastDialog() {
		let forecastId;

		this._configure({
			title: _("Import data from Forecast"),
			formSpec: {
				saveAction: () =>
					warnDialog({
						title: _("Import data from Forecast"),
						body: _(
							"Importing a forecast will replace the current budget content. Are you sure you want to continue?",
						),
						cssClass: "modal-content",
						callback: () =>
							this._saveBudgetFromForecastId(forecastId),
					}).show(),
			},
			okLabel: _("Import"),
			formBuilder: (form) => {
				form.newDropDownField({
					label: _("Forecast"),
					accessor: () => forecastId,
					mutator: (selectedForecastId) =>
						(forecastId = selectedForecastId),
					items: this._makeForecastDropdownItems(),
				});
			},
		});
	}

	async _saveBudgetFromForecastId(forecastId) {
		const updatedBudget =
			await this._onSaveBudgetFromForecastId(forecastId);
		this._getBudget().copyItemsFromBudget(updatedBudget);
		this._onImportDataCallback();
	}

	_makeForecastDropdownItems() {
		return this._allForecasts.map((forecast) => ({
			label: forecast.getName(),
			value: forecast.getId(),
		}));
	}

	_renderAddAccountSectionOn(html) {
		html.render([
			html.button(
				{
					class: "btn btn-block btn-default",
					"data-test": "add-income-cost-accounts",
					click: this._onAddIncomeAndCostAccountsCallback,
				},
				databaseIcon,
				_("All Accounts"),
			),
			this._renderAddRowButtonOn.bind(this),
		]);
	}

	_sectionRenderer(label, renderable) {
		return (html) =>
			html.div({ klass: "right-menu-section" }, (html) => {
				if (label) {
					html.label(label);
				}

				html.render(renderable);
			});
	}

	_updateDimension() {
		this._dimensionPicker.setDimensionTree(
			this._getBudget().getFiscalYear().getDimensionTree(),
		);

		this._dimensionPicker.setSelectedDimension(
			this._budgetReport.getDimension(),
		);
	}

	_getBudget() {
		return this._budgetReport.getBudget();
	}
}
